<template>
  <ModalStep :id="'edit-profile-picture-content'" :header="{ title: t('auth.editProfile.picture.title') }">
    <template #bodyContent>
      <div v-if="imageUrlRef" class="picture-uploaded-container">
        <div class="picture-uploaded">
          <img v-if="imageUrlRef" :src="imageUrlRef" alt="uploaded picture" />
        </div>
        <div class="picture-side-info">
          <p>
            <strong>{{ accountRef?.firstName }}</strong
            >, <br />
            {{ t('auth.editProfile.picture.uploaded') }}
          </p>
          <button type="button" @click="changeProfilePicture">{{ t('ui.button.change') }}</button>
        </div>
      </div>
      <FileUpload v-else accept=".jpg, .png, .heic, .jpeg" :callback="(file) => (pictureFileRef = file)">
        <template #instructions>
          <p>{{ t('ui.input.file.instructions.maxSize') }} 25MB</p>
          <p>{{ t('ui.input.file.instructions.format') }} .jpg, .png, .HEIC</p>
          <p>{{ t('ui.input.file.instructions.minSize') }} 500x500</p>
        </template>
      </FileUpload>
      <div class="c-info-wrapper">
        <p class="c-info-label">
          <strong>{{ t('auth.editProfile.picture.examples.title') }}</strong>
        </p>
        <p class="c-info-text">
          {{ t('auth.editProfile.picture.examples.body') }}
        </p>
      </div>
      <div class="examples-pictures-container">
        <div class="picture">
          <img src="/images/picture-example-1.png" alt="example picture 1" />
        </div>
        <div class="picture">
          <img src="/images/picture-example-2.png" alt="example picture 2" />
        </div>
        <div class="picture">
          <img src="/images/picture-example-3.png" alt="example picture 3" />
        </div>
      </div>
    </template>
    <template #footer>
      <div class="c-btns-container">
        <CustomButton
          usage="button"
          type="button"
          color="white"
          @emit:click="closeModal"
          :text="t('ui.button.cancel')"
        />
        <CustomButton
          usage="button"
          type="button"
          color="primary"
          data-test-id="submit-profile-picture-button"
          :is-loading="isSubmittingRef"
          @emit:click="submitForm"
          :text="t('ui.button.confirm')"
          icon-position="right"
        />
      </div>
    </template>
  </ModalStep>
</template>

<script setup lang="ts">
import { useMutation } from '@tanstack/vue-query'
import heic2any from 'heic2any'
import { ref, watch, watchEffect } from 'vue'
import type { ZodError } from 'zod'
import z from 'zod'

import { updateUserMutation } from '@/features/auth/api'
import useAccount from '@/features/auth/hooks/use-account.hook'
import { selectGetSelectedProgramId } from '@/features/programs/stores/programs/programs.selectors'
import programsStore from '@/features/programs/stores/programs/programs.store'
import CustomButton from '@/features/ui/components/button/custom-button.vue'
import FileUpload from '@/features/ui/components/file-upload/file-upload.vue'
import ModalStep from '@/features/ui/components/modal/modal-step/modal-step.vue'
import modalsStore from '@/features/ui/store/modal/modal.store'
import useToast from '@/hooks/use-toasts.hook'
import { useI18n } from '@/lib/i18n'
import { PROFILE_PICTURE_BUCKET_URL } from '@/utils/config/constants'
import { invalidateAccount, invalidateGroups, invalidateUser } from '@/utils/lib/vue-query'

const { t } = useI18n()
const toast = useToast()
const selectedProgramIdRef = selectGetSelectedProgramId(programsStore)
const { accountRef } = useAccount()

const pictureFileRef = ref<null | File>(null)
const convertedPictureRef = ref<null | File>(null)

const imageUrlRef = ref<string | null>(null)

watchEffect(() => {
  if (convertedPictureRef.value) {
    imageUrlRef.value = URL.createObjectURL(convertedPictureRef.value)
    return
  }
  if (accountRef.value?.picture) {
    imageUrlRef.value = `${PROFILE_PICTURE_BUCKET_URL}${accountRef.value.picture}`
    return
  }
})

watch(pictureFileRef, (newPictureFile) => {
  if (!newPictureFile) return

  if (newPictureFile.type === 'image/heic') {
    heic2any({ blob: newPictureFile, toType: 'image/jpeg', quality: 0.5 })
      .then((blob) => {
        const convertedFile = new File([blob as Blob], newPictureFile.name.replace('.heic', '.jpg'), {
          type: 'image/jpeg',
        })
        convertedPictureRef.value = convertedFile
      })
      .catch(() => {
        // Sentry WEB_APP-14G : libheif ne décode pas tous les HEIC
        // (« ERR_LIBHEIF format not supported »). Sans ce catch la promesse
        // rejetait en silence : rien à l'écran, une UnhandledRejection dans Sentry.
        pictureFileRef.value = null
        convertedPictureRef.value = null
        toast?.error(t('auth.editProfile.picture.conversionError'))
      })
  } else {
    convertedPictureRef.value = newPictureFile
  }
})

const validationSchema = z.object({
  profilePicture: z.instanceof(File),
})

const isSubmittingRef = ref(false)

const { mutateAsync: updateUserProfilePicture } = useMutation({
  mutationFn: updateUserMutation,
})

async function submitForm() {
  try {
    isSubmittingRef.value = true
    const data = await validationSchema.parseAsync({ profilePicture: convertedPictureRef.value! })
    await updateUserProfilePicture({ picture: data.profilePicture, userId: accountRef.value!.id! })
    await invalidateUser(accountRef.value!.id!)
    await invalidateAccount()
    await invalidateGroups(selectedProgramIdRef.value!)
    toast?.success(t('auth.editProfile.success'))
    closeModal()
  } catch (e: any) {
    const error: ZodError = e
    if (error.errors) {
      error.errors.forEach((err) => toast?.error(err.message))
    } else {
      toast?.error(t('error.common'))
    }
  } finally {
    isSubmittingRef.value = false
  }
}

function changeProfilePicture() {
  imageUrlRef.value = null
  convertedPictureRef.value = null
}

function closeModal() {
  modalsStore.toggleModal('profile-picture', false)
}
</script>

<style lang="scss">
@import './profile-picture-modal-content.scss';
</style>
